import {
  Drawer,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as PortableIcon } from '../../../../assets/icons/desktop-computer.svg';
import { ReactComponent as DesktopIcon } from '../../../../assets/icons/desktop.svg';
import { ReactComponent as MobileIcon } from '../../../../assets/icons/mobile.svg';
import { ReactComponent as TabletIcon } from '../../../../assets/icons/tablet.svg';
import EDButton from '../../../../atoms/EDButton/EDButton';
import EDCircularLoading from '../../../../atoms/EDCircularLoading/EDCircularLoading';
import Icon from '../../../../atoms/icon';
import BBJSONEditor from '../../../../components/JSONEditor/BBJSONEditor';
import i18nInstance from '../../../../i18n/i18n';
import FullPageLayout from '../../../../layouts/dashboardFullPage/FullPageLayout';
import { useLocalStorage } from '../../../../utils/hooks/useLocalStorage';
import { localStorageKeys } from '../../../../utils/localStorageKeys';
import './FullScreenPreview.scss';

const FullScreenPreview = ({
  isLoading,
  onClose,
  editorCodeString,
  onSetEditorCodeString,
  onHasError,
  onUpdatePreview,
  previewDataJSON,
  deviceMode,
  onGetMultilanguageKeys,
  previewLanguage,
  onHandlePreviewLanguageChange,
  readOnly
}) => {
  const { t } = useTranslation();
  const { getItem } = useLocalStorage();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const [uiActivitiesLanguage, setUiActivitiesLanguage] = useState('en');

  const iframeRef = useRef(null); // Crear una referencia para el iframe
  const [iframeLoaded, setIframeLoaded] = useState(false); // Crear un estado para verificar si el iframe ha terminado de cargar

  useEffect(() => {
    setUiActivitiesLanguage(
      getItem(localStorageKeys.language) || i18nInstance.language || 'en'
    );
  }, []);
  // Preview modes
  // desktop
  // tablet landscape w-1024px
  // tablet portrait w-768px
  // mobile w-416px

  const [previewMode, setPreviewMode] = useState(
    deviceMode === 'desktop' ? 'desktop' : 'mobile'
  );

  const previewTitle = {
    desktop: 'Desktop mode',
    landscape: 'Landscape mode (1024x768)',
    portrait: 'Portrait mode (768x1024)',
    portable: 'Portable mode (1358x610)',
    mobile: 'Mobile mode (360x740)'
  };

  const onToggleDrawer = () => setIsDrawerOpen(!isDrawerOpen);

  const onUpdateFullScreenPreview = async () => {
    onUpdatePreview();
    postMessageToIframe();
  };

  /* useEffect(() => {
    onUpdateFullScreenPreview();
  }, [previewLanguage]); */

  useEffect(() => {
    if (isLoading) return;
    onUpdateFullScreenPreview();
  }, [isLoading]);

  useEffect(() => {
    if (iframeLoaded) {
      postMessageToIframe();
    }
  }, [iframeLoaded, previewDataJSON]);

  // Función para enviar mensaje (podrías llamar a esta función basada en algún evento o efecto de React)
  const postMessageToIframe = () => {
    const dataToSend = {
      json: previewDataJSON,
      uiActivitiesLanguage: uiActivitiesLanguage,
      previewLanguage: previewLanguage
    };

    if (iframeRef.current) {
      iframeRef.current.contentWindow.postMessage(dataToSend, '*');
    }
  };

  window.addEventListener(
    'message',
    (event) => {
      // console.log('FullScreenPreview event', event);
      // Asegúrate de que el mensaje es del iframe que esperas
      // if (event.origin !== "http://url.de.tu.iframe") return;

      // Aquí puedes manejar las acciones requeridas en base al mensaje recibido
      let payload = event.data;

      if (payload === 'openKeyboard') {
        // Código para abrir el teclado aquí
      }
    },
    false
  );

  // Función que será llamada cuando el iframe haya terminado de cargarse
  const handleIframeLoad = () => {
    setIframeLoaded(true);
  };

  return (
    <div className='full-screen-preview'>
      <FullPageLayout
        left={
          <div className='full-screen-preview__edit'>
            <EDButton
              size='medium'
              onClick={onToggleDrawer}
              startIcon={<Icon type='edit' format='outline' />}
            >
              {t('Editor')}
            </EDButton>
          </div>
        }
        subtitle={previewTitle[previewMode] || 'Desktop mode'}
        right={
          <div className='full-screen-preview__header-right'>
            {deviceMode === 'mobile' && (
              <div
                className='full-screen-preview__mobile-btn'
                onClick={() => setPreviewMode('mobile')}
                title='Mobile mode (360x740)'
              >
                <MobileIcon />
              </div>
            )}
            <div
              className='full-screen-preview__portrait-btn'
              onClick={() => setPreviewMode('portrait')}
              title='Portrait mode (768x1024)'
            >
              <TabletIcon />
            </div>
            <div
              className='full-screen-preview__landscape-btn'
              onClick={() => setPreviewMode('landscape')}
              title='Landscape mode (1024x768)'
            >
              <TabletIcon />
            </div>
            <div
              className='full-screen-preview__portrait-btn'
              onClick={() => setPreviewMode('portable')}
              title='Portable mode (1358x610)'
            >
              <PortableIcon />
            </div>

            {deviceMode === 'desktop' && (
              <div
                className='full-screen-preview__desktop-btn'
                onClick={() => setPreviewMode('desktop')}
                title='Desktop mode'
              >
                <DesktopIcon />
              </div>
            )}

            <div className='full-screen-preview__close' onClick={onClose}>
              <Icon type='clear' format='outline' />
            </div>
          </div>
        }
        fullWidth
        fullScreenModal
      >
        <Drawer
          className='full-screen-preview__drawer'
          anchor={'left'}
          open={isDrawerOpen}
          onClose={onToggleDrawer}
        >
          <Grid
            container
            spacing={3}
            justifyContent='space-between'
            alignItems='center'
          >
            <Grid item>
              <Typography variant='h2'>{t('editor_subtitle')}</Typography>
            </Grid>
            <Grid item>
              <EDButton
                variant='outlined'
                size='large'
                onClick={onUpdateFullScreenPreview}
              >
                {t('update_preview_button')}
              </EDButton>
            </Grid>
            <Grid item xs={12}>
              <BBJSONEditor
                defaultJSON={editorCodeString}
                onSetEditorCodeString={onSetEditorCodeString}
                onHasError={onHasError}
                source={'fullscreen'}
                readOnly={readOnly}
              />
            </Grid>
          </Grid>
        </Drawer>

        <Grid
          container
          xs={12}
          justifyContent='center'
          sx={{ marginTop: '1em' }}
        >
          {!previewDataJSON && <EDCircularLoading fullScreen />}

          {onGetMultilanguageKeys() && (
            <div className='full-screen-preview__languages-selector'>
              <ToggleButtonGroup
                color='primary'
                value={previewLanguage}
                exclusive
                onChange={onHandlePreviewLanguageChange}
                aria-label='Language'
                size='small'
              >
                {onGetMultilanguageKeys().map((langItem, index) => (
                  <ToggleButton value={langItem} key={index}>
                    {langItem.toUpperCase()}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </div>
          )}

          <div
            className={`fullscreen-activity-preview-container fullscreen-activity-preview-container--${previewMode}`}
          >
            <div className='app-practice-page'>
              <iframe
                src='/preview'
                ref={iframeRef}
                title='Preview'
                width='100%'
                height='100%'
                onLoad={handleIframeLoad} // Aquí estableces el manejador de eventos para cuando el iframe ha terminado de cargarse
              >
                Your browser does not support iframes.
              </iframe>
              {/* previewDataJSON && false && (
                <ActivityBlueberryComponent
                  activityType={ACTIVITY_TYPES.PREVIEW}
                  activity={previewDataJSON}
                  uiLanguage={uiActivitiesLanguage}
                  mathRenderEngine='katex'
                  exerciseLanguage={previewLanguage}
                  pathAssetsSchool={`/assets/graphics/gamification`}
                />
              ) */}
            </div>
          </div>
        </Grid>
      </FullPageLayout>
    </div>
  );
};

export default FullScreenPreview;
